import { ODOO_BASE_URL } from '../../odooConfig';

const isLocalDevelopment =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const BASE_URL = isLocalDevelopment ? '' : ODOO_BASE_URL;

const API_PREFIX = '/api/employee-attendance/hr';

class EmployeeAttendanceHrAPI {
  constructor(baseURL = BASE_URL) {
    this.baseURL = baseURL;
    this.onUnauthorized = null;
  }

  setUnauthorizedHandler(handler) {
    this.onUnauthorized = handler;
  }

  async request(endpoint, params = {}, token = null, options = {}) {
    const { suppressUnauthorized = false } = options;
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'call',
        params,
        id: Date.now(),
      }),
    });

    if (!suppressUnauthorized && response.status === 401 && this.onUnauthorized) {
      this.onUnauthorized();
    }

    if (!response.ok) {
      const errorText = await response.text();
      const err = new Error(
        response.status === 404
          ? `Endpoint not found (404): ${endpoint}`
          : errorText || response.statusText
      );
      err.httpStatus = response.status;
      err.code = response.status;
      if (!suppressUnauthorized && response.status === 401 && this.onUnauthorized) {
        this.onUnauthorized();
      }
      throw err;
    }

    const data = await response.json();

    if (data.error) {
      const nested =
        data.error.data?.message ||
        data.error.data?.debug ||
        data.error.message ||
        'API request failed';
      // Prefer the human-readable Odoo message when present
      const message = String(nested)
        .replace(/^Odoo Server Error:?\s*/i, '')
        .split('\n')[0]
        .trim() || 'API request failed';
      const err = new Error(message);
      err.errorData = data.error;
      err.code = data.error.code;
      throw err;
    }

    const result = data.result ?? data;

    if (result?.success === false) {
      const code = result.code ?? 400;
      if (!suppressUnauthorized && (code === 401 || result.error === 'Unauthorized')) {
        if (this.onUnauthorized) this.onUnauthorized();
      }
      const message =
        result.message || result.error || data.error?.message || 'API request failed';
      const err = new Error(message);
      err.code = code;
      err.result = result;
      throw err;
    }

    return result;
  }

  // --- Auth ---

  ping() {
    return this.request(`${API_PREFIX}/ping`);
  }

  login(username, password, options = {}) {
    return this.request(`${API_PREFIX}/login`, { username, password }, null, options);
  }

  me(token, options = {}) {
    return this.request(`${API_PREFIX}/me`, {}, token, options);
  }

  logout(token) {
    return this.request(`${API_PREFIX}/logout`, {}, token);
  }

  // --- Departments & employees ---

  getDepartments(token) {
    return this.request(`${API_PREFIX}/departments`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  getEmployees(params, token) {
    return this.request(`${API_PREFIX}/employees`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getEmployee(employeeId, token) {
    return this.request(
      `${API_PREFIX}/employees/get`,
      { employee_id: Number(employeeId) },
      token,
      { suppressUnauthorized: true }
    );
  }

  createEmployee(params, token) {
    return this.request(`${API_PREFIX}/employees/create`, params, token, {
      suppressUnauthorized: true,
    });
  }

  updateEmployee(params, token) {
    return this.request(`${API_PREFIX}/employees/update`, params, token, {
      suppressUnauthorized: true,
    });
  }

  archiveEmployee(employeeId, active, token) {
    return this.request(
      `${API_PREFIX}/employees/archive`,
      { employee_id: Number(employeeId), active },
      token,
      { suppressUnauthorized: true }
    );
  }

  // --- Portal access ---

  createPortalAccess(params, token) {
    return this.request(`${API_PREFIX}/portal-access/create`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getPortalAccessFaculties(token) {
    return this.request(`${API_PREFIX}/portal-access/faculties`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  /**
   * List Odoo "Employee Attendance Portal Access" records (same tree as Odoo form).
   * Tries /portal-access then /portal-access/list.
   */
  async getPortalAccessList(params = {}, token) {
    try {
      return await this.request(`${API_PREFIX}/portal-access`, params, token, {
        suppressUnauthorized: true,
      });
    } catch (err) {
      if (err.httpStatus === 404 || err.code === 404) {
        return this.request(`${API_PREFIX}/portal-access/list`, params, token, {
          suppressUnauthorized: true,
        });
      }
      throw err;
    }
  }

  getPortalEmployees(token) {
    return this.request(`${API_PREFIX}/portal-employees`, {}, token, {
      suppressUnauthorized: true,
    });
  }

  // --- Attendance ---

  getAttendanceLines(params, token) {
    return this.request(`${API_PREFIX}/attendance-lines`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getAttendance(params, token) {
    return this.request(`${API_PREFIX}/attendance/get`, params, token, {
      suppressUnauthorized: true,
    });
  }

  createAttendance(params, token) {
    return this.request(`${API_PREFIX}/attendance/create`, params, token, {
      suppressUnauthorized: true,
    });
  }

  updateAttendance(params, token) {
    return this.request(`${API_PREFIX}/attendance/update`, params, token, {
      suppressUnauthorized: true,
    });
  }

  deleteAttendance(attendanceId, token) {
    return this.request(
      `${API_PREFIX}/attendance/delete`,
      { attendance_id: Number(attendanceId) },
      token,
      { suppressUnauthorized: true }
    );
  }

  checkInEmployee(params, token) {
    return this.request(`${API_PREFIX}/attendance/check-in`, params, token, {
      suppressUnauthorized: true,
    });
  }

  checkOutEmployee(params, token) {
    return this.request(`${API_PREFIX}/attendance/check-out`, params, token, {
      suppressUnauthorized: true,
    });
  }

  // --- Reports ---

  getReport(params, token) {
    return this.request(`${API_PREFIX}/report`, params, token, {
      suppressUnauthorized: true,
    });
  }

  // --- Hikvision (Odoo Attendance menus; may 404 until backend adds them) ---

  getHikvisionDevices(params = {}, token) {
    return this.request(`${API_PREFIX}/hikvision/devices`, params, token, {
      suppressUnauthorized: true,
    });
  }

  getHikvisionPunches(params = {}, token) {
    return this.request(`${API_PREFIX}/hikvision/punches`, params, token, {
      suppressUnauthorized: true,
    });
  }
}

const employeeAttendanceHrAPI = new EmployeeAttendanceHrAPI();
export default employeeAttendanceHrAPI;
